// W2Cook Billing Dashboard — Add / Edit Service modal

import { useState } from "react";
import type { Service } from "../data/services";
import type { ConnectorTemplate } from "../connectors/templates";
import { ENVIRONMENTS, ENV_CONFIG } from "../data/environments";
import { tokens as T } from "../theme/tokens";
import { Btn, Field, Modal, SectionLabel } from "./ui";

interface Props {
  service: Partial<Service> & { id: string };
  categories: string[];
  connectorTemplates: ConnectorTemplate[];
  configuredConnectors: Record<string, unknown>;
  onSave:   (s: Service) => void;
  onDelete: (id: string) => void;
  onClose:  () => void;
}

export function ServiceModal({ service, categories, connectorTemplates, configuredConnectors, onSave, onDelete, onClose }: Props) {
  const [form, setForm] = useState<Service>({
    id:             service.id ?? "",
    name:           service.name ?? "",
    category:       service.category ?? categories[0] ?? "Other",
    environment:    service.environment ?? "all",
    monthlyCost:    service.monthlyCost ?? 0,
    status:         service.status ?? "active",
    icon:           service.icon ?? "🔧",
    note:           service.note ?? "",
    renewsOn:       service.renewsOn ?? null,
    apiConnectorId: service.apiConnectorId ?? null,
    budgetLimit:    service.budgetLimit ?? 0,
    createdAt:      service.createdAt ?? new Date().toISOString(),
    updatedAt:      new Date().toISOString(),
  });

  const set = <K extends keyof Service>(k: K, v: Service[K]) =>
    setForm((p: Service) => ({ ...p, [k]: v, updatedAt: new Date().toISOString() }));

  const isNew = !service.createdAt;

  const addCategory = () => {
    const n = prompt("New category name:");
    if (n?.trim()) set("category", n.trim());
  };

  return (
    <Modal title={isNew ? "Add Service" : `Edit — ${service.name}`} onClose={onClose}>
      <div style={{ display: "grid", gridTemplateColumns: "64px 1fr", gap: "0 12px" }}>
        <Field label="ICON" value={form.icon} onChange={(v) => set("icon", v)} placeholder="🔧" />
        <Field label="SERVICE NAME" value={form.name} onChange={(v) => set("name", v)} placeholder="e.g. Stripe" />
      </div>

      <SectionLabel>CATEGORY</SectionLabel>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>
        {categories.map((c) => (
          <button key={c} onClick={() => set("category", c)} style={{
            padding: "4px 10px", borderRadius: 6, fontSize: 12, cursor: "pointer", fontFamily: "inherit",
            background: form.category === c ? `${T.ember}20` : T.surfaceAlt,
            border: `1px solid ${form.category === c ? T.ember : T.border}`,
            color: form.category === c ? T.ember : T.textMuted,
          }}>{c}</button>
        ))}
        <button onClick={addCategory} style={{
          padding: "4px 10px", borderRadius: 6, fontSize: 12, cursor: "pointer",
          background: T.surfaceAlt, border: `1px dashed ${T.border}`, color: T.textDim, fontFamily: "inherit",
        }}>+ New</button>
      </div>

      <Field
        label="MONTHLY COST (USD)"
        type="number"
        value={form.monthlyCost}
        onChange={(v) => set("monthlyCost", parseFloat(v) || 0)}
        placeholder="0.00"
      />

      <SectionLabel>STATUS</SectionLabel>
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {(["active", "paused", "cancelled"] as const).map((s) => (
          <button key={s} onClick={() => set("status", s)} style={{
            flex: 1, padding: "8px 0", borderRadius: 8, fontSize: 13,
            cursor: "pointer", fontFamily: "inherit", textTransform: "capitalize",
            background: form.status === s ? `${T.sage}20` : T.surfaceAlt,
            border: `1px solid ${form.status === s ? T.sage : T.border}`,
            color: form.status === s ? T.sage : T.textMuted,
          }}>{s}</button>
        ))}
      </div>

      <Field label="RENEWS ON (YYYY-MM-DD)" value={form.renewsOn ?? ""} onChange={(v) => set("renewsOn", v || null)} placeholder="2026-08-01" />
      <SectionLabel>ENVIRONMENT</SectionLabel>
      <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
        {([...ENVIRONMENTS, "all"] as const).map(env => {
          const cfg = env === "all" ? { label: "All Environments", color: T.textMuted } : ENV_CONFIG[env];
          return (
            <button key={env} onClick={() => set("environment", env)} style={{
              padding: "4px 10px", borderRadius: 6, fontSize: 12, cursor: "pointer", fontFamily: "inherit",
              background: form.environment === env ? `${cfg.color}20` : T.surfaceAlt,
              border: `1px solid ${form.environment === env ? cfg.color : T.border}`,
              color: form.environment === env ? cfg.color : T.textMuted,
            }}>{env === "all" ? "All" : ENV_CONFIG[env as keyof typeof ENV_CONFIG].badge}</button>
          );
        })}
      </div>

      <Field label="BUDGET LIMIT (USD/mo) — 0 = no limit" type="number" value={form.budgetLimit} onChange={v => set("budgetLimit", parseFloat(v) || 0)} placeholder="0" />

      <Field label="NOTES" value={form.note} onChange={(v) => set("note", v)} rows={2} placeholder="Context, upgrade path…" />

      <SectionLabel>API CONNECTOR</SectionLabel>
      <select
        value={form.apiConnectorId ?? ""}
        onChange={(e) => set("apiConnectorId", e.target.value || null)}
        style={{
          width: "100%", background: T.bg, border: `1px solid ${T.border}`,
          borderRadius: 8, color: T.text, padding: "9px 12px",
          fontSize: 13, marginBottom: 20, outline: "none", fontFamily: "inherit",
        }}
      >
        <option value="">None (manual entry only)</option>
        {connectorTemplates.map((c) => (
          <option key={c.id} value={c.id}>
            {configuredConnectors[c.id] ? "✅" : "⚠️"} {c.name}
          </option>
        ))}
      </select>

      <div style={{ display: "flex", gap: 10 }}>
        {!isNew && (
          <Btn variant="danger" onClick={() => onDelete(form.id)}>Delete</Btn>
        )}
        <Btn variant="ghost" onClick={onClose} style={{ flex: 1 }}>Cancel</Btn>
        <Btn variant="primary" onClick={() => onSave(form)} style={{ flex: 1 }}>Save</Btn>
      </div>
    </Modal>
  );
}
